import { notifyAuthSessionChanged } from "./auth-session";
import { managementReferenceCache } from "./managementReferenceCache";

export interface AuthenticatedUser {
  id: string;
  name?: string;
  username?: string;
  isActive?: boolean;
}

export interface SessionSidebarItem {
  id: string; code: string; name: string; path?: string; sequence?: number;
  sidebar: boolean; children?: SessionSidebarItem[];
}

export interface AuthenticatedSession {
  accessToken: string;
  refreshToken: string;
  user: AuthenticatedUser;
  permissions?: string[];
  sidebar?: SessionSidebarItem[];
  authorization?: { kind: "staff" | "invitation" } | { kind: "group-summary"; saleId: string };
}

export const sessionKey = (accessKey: string) => `${accessKey}:session`;

export function validateAuthenticatedSession(value: unknown): AuthenticatedSession {
  const session = value as AuthenticatedSession;
  const backendVerified = session?.authorization?.kind === "invitation" || session?.authorization?.kind === "group-summary";
  if (!session || typeof session.accessToken !== "string" || !session.accessToken.trim() ||
    typeof session.refreshToken !== "string" || !session.refreshToken.trim() ||
    (!backendVerified && (!session.user || typeof session.user.id !== "string" || !session.user.id.trim() || session.user.isActive === false))) {
    throw new Error("Invalid authenticated user response.");
  }
  const permissions = Array.isArray(session.permissions) && session.permissions.every(code => typeof code === "string")
    ? [...session.permissions] : undefined;
  const storedAuthorization = session.authorization as { kind?: string; saleId?: unknown; taskId?: unknown } | undefined;
  const summaryId = storedAuthorization?.saleId ?? storedAuthorization?.taskId;
  const authorization = session.authorization?.kind === "group-summary" && typeof summaryId === "string"
    ? { kind: "group-summary" as const, saleId: summaryId }
    : session.authorization?.kind === "invitation" ? { kind: "invitation" as const }
    : session.authorization?.kind === "staff" ? { kind: "staff" as const } : undefined;
  const sidebar = Array.isArray(session.sidebar) ? session.sidebar : undefined;
  const user = backendVerified
    ? { ...(session.user && typeof session.user === "object" && !Array.isArray(session.user) ? session.user : {}), id: typeof session.user?.id === "string" ? session.user.id : "" }
    : session.user;
  return { accessToken: session.accessToken, refreshToken: session.refreshToken, user, permissions, sidebar, authorization };
}

export function readAuthenticatedSession(accessKey: string): AuthenticatedSession | null {
  try {
    const session = validateAuthenticatedSession(JSON.parse(localStorage.getItem(sessionKey(accessKey)) || "null"));
    return session.accessToken === localStorage.getItem(accessKey) ? session : null;
  } catch { return null; }
}

export function clearAuthenticatedSessions() {
  for (const key of ["authToken", "refreshToken", "sellSupportAuthToken", "sellSupportRefreshToken", sessionKey("authToken"), sessionKey("sellSupportAuthToken")]) {
    localStorage.removeItem(key);
  }
  managementReferenceCache.clear();
}

export function replaceAuthenticatedSession(value: unknown, saleSession: boolean) {
  const session = validateAuthenticatedSession(value);
  const accessKey = saleSession ? "sellSupportAuthToken" : "authToken";
  const refreshKey = saleSession ? "sellSupportRefreshToken" : "refreshToken";
  clearAuthenticatedSessions();
  try {
    localStorage.setItem(accessKey, session.accessToken);
    localStorage.setItem(refreshKey, session.refreshToken);
    localStorage.setItem(sessionKey(accessKey), JSON.stringify(session));
  } catch (failure) {
    clearAuthenticatedSessions();
    notifyAuthSessionChanged();
    throw failure;
  }
  notifyAuthSessionChanged();
}
